import {
    areaBottomColor,
    areaTopColor,
    baselineTopFillColor1,
    baselineTopFillColor2,
    buyColor,
    candleUpColorAlpha,
} from '../assets/colors'
import { ChartType, chartTypeFromJSON } from '../proto/generated/chart'
import { defaultAlertSettings, defaultTradingSettings } from './chart'
import { defaultCanvasSettings } from './canvasSettingsDefaults'
import { defaultEventsSettings } from './eventsSettingsDefaults'
import { defaultScalesAndLinesSettings } from './scalesAndLinesDefaults'
import { defaultStatusLineSettings } from './statusLineDefaults'

/** Primary series defaults keyed by chart type — same table as chart settings "Apply Default". */
export const DEFAULT_SERIES_SETTINGS_BY_CHART_TYPE = {
    [ChartType.CANDLESTICK]: {
        visible: true,
        upColor: buyColor,
        downColor: '#ef5350',
        borderVisible: false,
        borderUpColor: buyColor,
        borderDownColor: '#ef5350',
        wickVisible: true,
        wickUpColor: buyColor,
        wickDownColor: '#ef5350',
    },
    [ChartType.HLC_AREA]: {
        highLineColor: buyColor,
        lowLineColor: '#ef5350',
        closeLineColor: '#2862ff',
        topFillColor: '#00bcd440',
        bottomFillColor: '#e91e6340',
        highLineVisible: true,
        lowLineVisible: false,
    },
    [ChartType.STEP_LINE_CHART]: {
        color: '#2962FF',
    },
    [ChartType.HIGH_LOW]: {
        visible: true,
        upColor: '#2862ff',
        downColor: '#2862ff',
        borderVisible: true,
        borderUpColor: '#2862ff',
        borderDownColor: '#2862ff',
        wickVisible: false,
        wickUpColor: buyColor,
        wickDownColor: buyColor,
    },
    [ChartType.LINE]: {
        color: '#2196f3',
        source: 'close',
    },
    [ChartType.LINE_WITH_MARKERS]: {
        color: '#2196f3',
        source: 'close',
    },
    [ChartType.AREA]: {
        topColor: areaTopColor,
        bottomColor: areaBottomColor,
        lineColor: buyColor,
        source: 'close',
    },
    [ChartType.BAR]: {
        upColor: buyColor,
        downColor: '#ef5350',
        thinBars: false,
    },
    [ChartType.LINE_BREAK]: {
        upColor: buyColor,
        downColor: '#ef5350',
        borderVisible: true,
        borderUpColor: buyColor,
        borderDownColor: '#ef5350',
    },
    [ChartType.VOLUME_CANDLESTICK]: {
        upColor: buyColor,
        downColor: '#ef5350',
        borderVisible: true,
        borderUpColor: buyColor,
        borderDownColor: '#ef5350',
        wickUpColor: buyColor,
        wickDownColor: '#ef5350',
    },
    [ChartType.HEIKIN_ASHI]: {
        visible: true,
        upColor: buyColor,
        downColor: '#ef5350',
        borderVisible: false,
        borderUpColor: buyColor,
        borderDownColor: '#ef5350',
        wickVisible: true,
        wickUpColor: buyColor,
        wickDownColor: '#ef5350',
    },
    [ChartType.HOLLOW_CANDLES]: {
        visible: true,
        upColorHollow: buyColor,
        downColorHollow: '#ef5350',
        borderVisibleHollow: false,
        borderUpColorHollow: buyColor,
        borderDownColorHollow: '#ef5350',
        wickVisibleHollow: true,
        wickUpColorHollow: buyColor,
        wickDownColorHollow: '#ef5350',
    },
    [ChartType.HISTOGRAM]: {
        upColor: candleUpColorAlpha,
        downColor: '#ef535080',
    },
    [ChartType.COLUMN]: {
        upColor: candleUpColorAlpha,
        downColor: '#ef535080',
    },
    [ChartType.BASELINE]: {
        source: 'close',
        baseValue: 50,
        topLineColor: buyColor,
        bottomLineColor: '#f23645',
        topFillColor1: baselineTopFillColor1,
        topFillColor2: baselineTopFillColor2,
        bottomFillColor1: '#f2364500',
        bottomFillColor2: '#f2364547',
    },
    [ChartType.KAGI]: {
        upColor: buyColor,
        downColor: '#ef5350',
        projectionUpColor: '#a9dcc3',
        projectionDownColor: '#f5a6ae',
        boxMethod: 'traditional',
        atrLength: 14,
        percentageLTP: 1,
        reversalAmount: 2,
    },
    [ChartType.POINT_AND_FIGURE]: {
        upColor: buyColor,
        downColor: '#ef5350',
        boxMethod: 'atr',
        boxSize: 1,
        atrLength: 14,
        percentage: 1,
        reversalAmount: 3,
    },
    [ChartType.RENKO]: {
        upColor: buyColor,
        downColor: '#ef5350',
        borderVisible: true,
        borderUpColor: buyColor,
        borderDownColor: '#ef5350',
        wickVisible: true,
        wickUpColor: buyColor,
        wickDownColor: '#ef5350',
        boxMethod: 'atr',
        boxSize: 10,
        atr: 14,
        percentage: 1,
        projectionUpColor: '#a9dcc3',
        projectionDownColor: '#f5a6ae',
        projectionUpBorderColor: '#a9dcc3',
        projectionDownBorderColor: '#f5a6ae',
    },
} as const

export function resolveChartTypeValue(chartType: ChartType | string | undefined | null): ChartType {
    if (chartType == null) return ChartType.CANDLESTICK
    return typeof chartType === 'string' ? chartTypeFromJSON(chartType) : chartType
}

export function getDefaultSeriesSettingsForChartType(
    chartType: ChartType | string | undefined | null,
): Record<string, unknown> {
    const resolved = resolveChartTypeValue(chartType)
    const defaults = DEFAULT_SERIES_SETTINGS_BY_CHART_TYPE[
        resolved as keyof typeof DEFAULT_SERIES_SETTINGS_BY_CHART_TYPE
    ]
    return defaults ? { ...defaults } : {}
}

export function getDefaultChartConfigSettingsPatch() {
    return {
        statusLineSettings: { ...defaultStatusLineSettings },
        tradingSettings: { ...defaultTradingSettings },
        alertSettings: { ...defaultAlertSettings },
        scalesAndLinesSettings: { ...defaultScalesAndLinesSettings },
        canvasSettings: { ...defaultCanvasSettings },
        eventsSettings: { ...defaultEventsSettings },
    }
}
